import React, { useState } from 'react';
import { Users, Zap, Settings, CheckCircle, AlertTriangle } from 'lucide-react';

interface TeamMember {
  id: string;
  name: string;
  role: string;
  capacity: number;
  currentLoad: number;
  skills: string[];
  availability: 'available' | 'busy' | 'unavailable';
}

interface AutoAssignmentEngineProps {
  projectId: string;
  userRole: 'client' | 'vendor' | 'admin';
}

export const AutoAssignmentEngine: React.FC<AutoAssignmentEngineProps> = ({ 
  projectId, 
  userRole 
}) => {
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(true);
  const [teamMembers] = useState<TeamMember[]>([
    {
      id: 'tm-001',
      name: 'Sarah Johnson',
      role: 'Project Manager',
      capacity: 40,
      currentLoad: 32,
      skills: ['project-management', 'client-relations', 'planning'],
      availability: 'available'
    },
    {
      id: 'tm-002',
      name: 'Mike Chen',
      role: 'Data Engineer',
      capacity: 40,
      currentLoad: 38,
      skills: ['data-migration', 'etl', 'database-design'],
      availability: 'busy'
    },
    {
      id: 'tm-003',
      name: 'Lisa Rodriguez',
      role: 'Implementation Specialist',
      capacity: 40,
      currentLoad: 25,
      skills: ['tyler-erp', 'configuration', 'training'],
      availability: 'available'
    }
  ]);

  const [assignmentRules] = useState([
    {
      id: 'rule-001',
      name: 'Load Balancing',
      description: 'Assign to team member with lowest current workload',
      enabled: true,
      priority: 1
    },
    {
      id: 'rule-002',
      name: 'Skill Matching',
      description: 'Match tasks to team members with relevant skills',
      enabled: true,
      priority: 2
    },
    {
      id: 'rule-003',
      name: 'Availability Check',
      description: 'Only assign to available team members',
      enabled: true,
      priority: 3
    }
  ]);

  const getAvailabilityColor = (availability: string) => {
    switch (availability) {
      case 'available': return 'text-[#8A9150] bg-[#8A9150]/10 border-[#8A9150]/20';
      case 'busy': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'unavailable': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getUtilizationColor = (utilization: number) => {
    if (utilization < 70) return 'text-[#8A9150]';
    if (utilization < 90) return 'text-yellow-600';
    return 'text-red-600';
  };

  const simulateAutoAssignment = () => {
    console.log('Simulating auto-assignment with current rules...');
  };

  return (
    <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Zap className="w-5 h-5 text-[#4267A1] mr-2" />
          Auto-Assignment Engine
        </h3>
        
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-600">Auto-assign tasks:</span>
          <button
            onClick={() => setAutoAssignEnabled(!autoAssignEnabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              autoAssignEnabled ? 'bg-[#8A9150]' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                autoAssignEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h4 className="font-medium text-gray-900 mb-4 flex items-center">
            <Users className="w-4 h-4 text-[#4267A1] mr-2" />
            Team Capacity
          </h4>
          
          <div className="space-y-3">
            {teamMembers.map((member) => {
              const utilization = Math.round((member.currentLoad / member.capacity) * 100);
              
              return (
                <div key={member.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h5 className="font-medium text-gray-900">{member.name}</h5>
                      <p className="text-sm text-gray-600">{member.role}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getAvailabilityColor(member.availability)}`}>
                      {member.availability.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          utilization < 70 ? 'bg-[#8A9150]' : 
                          utilization < 90 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${utilization}%` }}
                      ></div>
                    </div>
                    <span className={`text-sm font-medium ${getUtilizationColor(utilization)}`}>
                      {utilization}%
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap gap-1">
                    {member.skills.map((skill) => (
                      <span key={skill} className="px-2 py-1 bg-[#4267A1]/10 text-[#4267A1] text-xs rounded">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-4 flex items-center">
            <Settings className="w-4 h-4 text-[#4267A1] mr-2" />
            Assignment Rules
          </h4>
          
          <div className="space-y-3 mb-6">
            {assignmentRules.map((rule) => (
              <div key={rule.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {rule.enabled ? (
                      <CheckCircle className="w-4 h-4 text-[#8A9150]" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-gray-400" />
                    )}
                    <h5 className="font-medium text-gray-900">{rule.name}</h5>
                  </div>
                  <span className="text-xs text-gray-500">Priority {rule.priority}</span>
                </div>
                <p className="text-sm text-gray-600">{rule.description}</p>
              </div>
            ))}
          </div>
          
          <div className="space-y-3">
            <button
              onClick={simulateAutoAssignment}
              disabled={!autoAssignEnabled}
              className="w-full px-4 py-3 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Simulate Assignment
            </button>
            
            <button className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium">
              Configure Rules
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 text-xs text-gray-500">
        <p>• Auto-assignment uses existing CapacityEngineBee infrastructure</p>
        <p>• All assignments are logged for audit and can be manually overridden</p>
        <p>• Rules are evaluated in priority order for optimal task distribution</p>
      </div>
    </div>
  );
};
